import { useCallback, useMemo, type Dispatch, type ReactElement, type SetStateAction } from 'react'
import { useRequiredParams } from '@util'
import { isEmpty, isNotEmpty, type DateRange as ReportDateRange, type TeamId, type WithProgramId } from '@terros/common'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Group } from '@mantine/core'
import {
  getProgramPermissionIds,
  mergeGetHookResponses,
  useDefaultTeam,
  usePermissionCheck,
  useProgram,
  useServerReportState,
  useTranslation,
  type ServerReportFilter,
} from '@front/model'
import {
  ChooseDates,
  NotReady,
  Page,
  type DateRange,
  TButton,
  TeamMultiSelectFilter,
  ToggleColumns,
  useRangeQueryState,
  useReportColumnVariantToggle,
  type MultiSelectFilterBehavior,
} from '@components'
import { useDisplayData } from '../stats/state/useDisplayData'
import { useStatsClientState, useTableRows } from '../stats/state'
import { SetGrouping } from '../stats/SetGrouping'
import { FilterButton } from '../stats/filter/FilterButton'
import { DownloadCsv, StatsTable } from '../stats/components'
import { ArchivedUsersSelect } from '../stats/ArchivedUsersSelect'
import { useProgramUserStatusFilter } from './useProgramUserStatusFilter'
import { useProgramStatsColumns } from './useProgramStatsColumns'
import { FilterProgramUserStatus } from './FilterProgramUserStatus'

function ProgramView(): ReactElement {
  const { programId } = useRequiredParams<WithProgramId>()
  const { t } = useTranslation()
  const programState = useProgram(programId)
  const { data: program } = programState
  const { data: canEdit } = usePermissionCheck({
    permissionType: 'program:manage',
    targetId: program ? getProgramPermissionIds(program) : undefined,
  })
  const clientState = useStatsClientState('PROGRAM_STATS')
  const {
    filters,
    setFilters,
    range,
    setRange,
    grouping,
    setGrouping,
    archivedUsersDisplay,
    setArchivedUsersDisplay,
    teamIds,
    setTeamIds: _setTeamIds,
  } = clientState

  const { programUserStatus, setProgramUserStatus } = useProgramUserStatusFilter('PROGRAM_STATS')

  const columnState = useProgramStatsColumns({ programId, filters, grouping, storageKey: 'PROGRAM_STATS_COLUMNS' })

  const { displayColumns, fetchColumns, setSelectedColumnIds, selectedColumnIds, tableColumns, variantMap } =
    columnState
  const renderColumnToggle = useReportColumnVariantToggle(variantMap)

  const filter = useMemo((): ServerReportFilter => {
    return {
      start: range?.[0]?.valueOf(),
      end: range?.[1]?.valueOf(),
      teamIds: isNotEmpty(teamIds) ? teamIds : undefined,
      grouping,
      programUserStatus,
    }
  }, [range, teamIds, grouping, programUserStatus])

  const { data: canViewAll } = usePermissionCheck({
    permissionType: 'team:list',
    targetId: program?.companyId,
  })
  const defaultTeam = useDefaultTeam()
  const programStartDateState = useRangeQueryState(
    'PROGRAM_STATS-program-user-start-date',
    { type: 'allTime' },
    { start: 'programStart', end: 'programEnd' }
  )
  const programUserStartDate = useMemo(
    () => toReportDateRange(programStartDateState.range),
    [programStartDateState.range]
  )
  const serverFilter = useMemo((): ServerReportFilter => {
    return { ...filter, programUserStartDate }
  }, [filter, programUserStartDate])

  const serverState = useServerReportState({
    filter: serverFilter,
    includeUsersWithNoData: 'none',
    columns: fetchColumns,
    skipReportColumnsCheck: true,
    hideSystemUsers: true,
    showArchived: archivedUsersDisplay === 'aggregated' ? undefined : archivedUsersDisplay === 'individual',
  })
  const { data } = serverState

  const statRows = data?.format === 'table' ? data.rows : []
  const prefetchedTotalRow = data?.format === 'table' ? data.totalRow : undefined
  const tableState = useMemo(
    () => ({
      ...clientState,
      fetchColumns,
      displayColumns,
      setSelectedColumnIds,
    }),
    [clientState, fetchColumns, displayColumns, setSelectedColumnIds]
  )

  const { filtered } = useDisplayData({
    data: statRows,
    sortState: tableState.sortState,
    filters,
    filterColumns: fetchColumns,
  })

  const { tableRows, totalRow } = useTableRows(filtered, fetchColumns, prefetchedTotalRow)

  const state = mergeGetHookResponses([programState, serverState])

  const teamFilterBehavior: MultiSelectFilterBehavior = useMemo(() => {
    if (canViewAll) return { emptyBehavior: 'all', allValueBehavior: 'empty' }
    return { emptyBehavior: 'none', allowEmpty: false }
  }, [canViewAll])

  const setTeamIds: Dispatch<SetStateAction<TeamId[]>> = useCallback(
    (input) => {
      _setTeamIds((prev) => {
        const value = typeof input === 'function' ? input(prev) : input
        if (isEmpty(value) && !canViewAll) {
          return defaultTeam ? [defaultTeam] : []
        }
        return value
      })
    },
    [_setTeamIds, canViewAll, defaultTeam]
  )

  return (
    <Page>
      <Page.Heading {...state} title={program?.name ?? 'Program'} back goBackBehavior='history'>
        {canEdit && (
          <TButton component={Link} to={`/program/${programId}/setup`} icon='settings' variant='gray'>
            {t('Settings')}
          </TButton>
        )}
      </Page.Heading>
      <Page.FunctionBar>
        <Group>
          <ChooseDates range={range} setRange={setRange} />
          <SetGrouping grouping={grouping} setGrouping={setGrouping} />
          <ToggleColumns
            selectedColumns={selectedColumnIds}
            setSelectedColumns={setSelectedColumnIds}
            columns={tableColumns}
            renderColumnToggle={renderColumnToggle}
          />
          <TeamMultiSelectFilter
            aria-label={t('Team Filter')}
            allSelectedLabel={t('All teams')}
            compact
            size='md'
            value={teamIds}
            onChange={setTeamIds}
            behavior={teamFilterBehavior}
          />
          <ChooseDates
            range={programStartDateState.range}
            setRange={programStartDateState.setRange}
            labelPrefix={t('Program Start Date')}
          />
          <FilterProgramUserStatus setStatus={setProgramUserStatus} status={programUserStatus} />
          <FilterButton filters={filters} setFilters={setFilters} currentGrouping={grouping} />
        </Group>
        <Group>
          {grouping.type === 'user' && (
            <ArchivedUsersSelect value={archivedUsersDisplay} onChange={setArchivedUsersDisplay} />
          )}
          <DownloadCsv
            columns={displayColumns}
            filters={filters}
            grouping={grouping}
            reportInput={serverState.reportInput}
            rows={tableRows}
            totalRow={totalRow}
          />
        </Group>
      </Page.FunctionBar>
      <Page.Content>
        {state.isLoading || columnState.isLoading || !program || state.error ? (
          <NotReady {...state} isLoading={state.isLoading || columnState.isLoading} id='Program' />
        ) : (
          <StatsTable data={tableRows} totalRow={totalRow} tableState={tableState} />
        )}
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/program_/$programId_/view/')({
  component: ProgramView,
})

const toReportDateRange = (range: DateRange): ReportDateRange | undefined => {
  const [start, end] = range
  if (!start || !end) return undefined

  return {
    startTime: start.getTime(),
    endTime: end.getTime(),
  }
}
